import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, isIndividual } from './lib/auth';
import { isNativeApp } from './lib/native';
import Layout from './components/Layout';
import DashboardLayout from './components/DashboardLayout';
import LanguageWelcomeModal from './components/LanguageWelcomeModal';
// Eager: the homepage (canonical entry — flash-free) and the tiny 404.
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';

// Every other route is code-split so the initial bundle no longer ships recharts
// (Dashboard), the 1,289-line Messages page, Inventory, Settings, etc. They load
// on demand behind the layout-level <Suspense> boundaries.
const Exchange = lazy(() => import('./pages/Exchange'));
const DashboardExchange = lazy(() => import('./pages/DashboardExchange'));
const Market = lazy(() => import('./pages/Market'));
const Login = lazy(() => import('./pages/Login'));
const RegisterChoice = lazy(() => import('./pages/RegisterChoice'));
const Register = lazy(() => import('./pages/Register'));
const RegisterIndividual = lazy(() => import('./pages/RegisterIndividual'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardHome = lazy(() => import('./pages/DashboardHome'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Offers = lazy(() => import('./pages/Offers'));
const Operations = lazy(() => import('./pages/Operations'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const IndividualHome = lazy(() => import('./pages/IndividualHome'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const Team = lazy(() => import('./pages/Team'));
const Messages = lazy(() => import('./pages/Messages'));
const Join = lazy(() => import('./pages/Join'));
const Settings = lazy(() => import('./pages/Settings'));
const Feed = lazy(() => import('./pages/Feed'));
const Profile = lazy(() => import('./pages/Profile'));
const Discover = lazy(() => import('./pages/Discover'));
// Internal admin console — lazy-loaded so it stays out of the public bundle.
const AdminRoutes = lazy(() => import('./pages/admin/AdminRoutes'));

function ComeFunzionaRedirect() {
  useEffect(() => { window.location.replace('/#how-it-works'); }, []);
  return null;
}

function ProtectedRoute({ children, requireCompany }: { children: React.ReactNode; requireCompany?: boolean }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-stone-400">Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Vendor dashboard is company-only — individuals get sent to their own space.
  if (requireCompany && isIndividual(user)) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// On the Capacitor native build, skip the marketing landing and boot straight into the
// reserved area: /dashboard when logged in, /login otherwise. On web this returns <Landing/>
// unchanged (isNativeApp() is false), so web behaviour is identical and flash-free.
function HomeRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (!isNativeApp()) return <Landing />;
  if (loading) return <div className="flex items-center justify-center h-screen text-stone-400">Loading…</div>;
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
}

export default function App() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const applyDir = () => {
      document.documentElement.dir = i18n.dir();
      document.documentElement.lang = i18n.language;
    };
    applyDir();
    i18n.on('languageChanged', applyDir);
    return () => { i18n.off('languageChanged', applyDir); };
  }, [i18n]);

  return (
    <>
    <LanguageWelcomeModal />
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/exchange" element={<Exchange />} />
        <Route path="/exchange/:lotId" element={<Exchange />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterChoice />} />
        <Route path="/register/company" element={<Register />} />
        <Route path="/register/individual" element={<RegisterIndividual />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/network" element={<Feed />} />
        <Route path="/u/:userId" element={<Profile />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/join" element={<Join />} />
        <Route path="/come-funziona" element={<ComeFunzionaRedirect />} />
      </Route>
      {/* Individual (non-company) landing — cinematic Social/Exchange choice, full-screen. */}
      <Route
        path="/app"
        element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-stone-950 text-stone-400 text-sm">Loading…</div>}>
            <IndividualHome />
          </Suspense>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireCompany>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        {/* Home now merges the old Overview + Feed + Network; old links redirect there. */}
        <Route path="home" element={<Navigate to="/dashboard" replace />} />
        <Route path="discover" element={<Navigate to="/dashboard" replace />} />
        <Route path="overview" element={<Dashboard />} />
        <Route path="profile/:userId" element={<Profile />} />
        <Route path="operations" element={<Operations />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="market" element={<Market />} />
        <Route path="exchange" element={<DashboardExchange />} />
        <Route path="offers" element={<Offers />} />
        <Route path="leads" element={<Offers />} />
        <Route path="messages" element={<Messages />} />
        <Route path="settings" element={<Settings />} />
        <Route path="team" element={<Team />} />
      </Route>
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-stone-400 text-sm">Loading…</div>}>
            <AdminRoutes />
          </Suspense>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}
